import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LayerDefinitionDto } from '../../../../../api/gen';
import { LayerType } from '../../../../../api/gen/model/layerType';
import { DisplayState } from '../../../../../api/gen/model/displayState';
import { SaveLayoutPayload } from '../slot-list/slot-list.component';
import { LayerEditItemComponent } from '../layer-edit-item/layer-edit-item.component';
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { MatInput } from '@angular/material/input';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'app-layer-list',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatFormField,
        MatInput,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        LayerEditItemComponent,
        MatFormField,
        MatLabel,
        MatSelectModule
    ],
    templateUrl: './layer-list.component.html',
    styleUrl: './layer-list.component.scss'
})
export class LayerListComponent implements OnChanges, OnInit {
    @Input() savedLayers: LayerDefinitionDto[] | null | undefined = null;
    @Input() selectedState: DisplayState | null = null;
    @Input() saving = false;
    @Input() selectedTransientDurationSeconds: number | undefined = undefined;
    @Input() errorMessage: string | null = null;
    @Output() saveLayout = new EventEmitter<SaveLayoutPayload>();

    editingLayers = signal<Partial<LayerDefinitionDto>[]>([]);
    pendingImagesMap = signal<Map<number, File>>(new Map());
    editingTransientDuration = '';

    readonly layerTypes = Object.values(LayerType);

    get hasValidLayerKeys(): boolean {
        const keys = this.editingLayers().map(l => l.layerKey?.trim() ?? '');
        return keys.every(k => !!k) && new Set(keys).size === keys.length;
    }

    get canSave(): boolean {
        return this.editingLayers().length > 0 && this.hasValidLayerKeys;
    }

    get saveTooltip(): string {
        if (!this.hasValidLayerKeys) return 'All layers need unique, non-empty Layer Keys';
        return '';
    }

    ngOnInit(): void {
        this.errorMessage = null;
    }
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['selectedState'] || changes['savedLayers']) {
            this.errorMessage = null;

            this.editingLayers.set(this.savedLayers ? this.savedLayers.map(l => ({ ...l })) : []);
            this.pendingImagesMap.set(new Map());
            this.editingTransientDuration = this.selectedTransientDurationSeconds !== undefined
                ? String(this.selectedTransientDurationSeconds)
                : '';
        }
    }

    addLayer(): void {
        this.editingLayers.update(layers => [
            ...layers,
            {
                layerKey: '',
                layerType: LayerType.TextOverlay,
                zIndex: layers.length,
                isExpandable: false,
                position: { top: '0px', left: '0px', width: '100%', height: '100%' }
            }
        ]);
    }

    removeLayer(index: number): void {
        this.editingLayers.update(layers => layers.filter((_, i) => i !== index));
        this.pendingImagesMap.update(map => {
            const next = new Map<number, File>();
            map.forEach((file, i) => {
                if (i < index) next.set(i, file);
                else if (i > index) next.set(i - 1, file);
            });
            return next;
        });
    }

    updateLayer(index: number, updated: Partial<LayerDefinitionDto>): void {
        this.editingLayers.update(layers => {
            const next = [...layers];
            next[index] = updated;
            return next;
        });
    }

    getPendingFile(index: number): File | null {
        return this.pendingImagesMap().get(index) ?? null;
    }

    setPendingImage(index: number, file: File | null): void {
        this.pendingImagesMap.update(map => {
            const next = new Map(map);
            if (file) next.set(index, file);
            else next.delete(index);
            return next;
        });
    }

    submit(): void {
        if (!this.canSave) return;
        this.saveLayout.emit({
            slots: [],
            layers: this.editingLayers(),
            pendingImages: new Map(),
            pendingLayerImages: this.buildPendingLayerImagesPayload(),
            transientDurationSeconds: this.editingTransientDuration ? +this.editingTransientDuration : undefined
        });
    }

    private buildPendingLayerImagesPayload(): Map<string, File> {
        const pendingImages = new Map<string, File>();

        for (const [index, file] of this.pendingImagesMap()) {
            const layer = this.editingLayers()[index];
            const key = this.getLayerImageKey(layer);
            if (key) {
                pendingImages.set(key, file);
            }
        }

        return pendingImages;
    }

    private getLayerImageKey(layer: Partial<LayerDefinitionDto> | undefined): string | null {
        const dbId = layer?.id?.trim();
        const layerKey = layer?.layerKey?.trim();
        if (dbId) {
            return JSON.stringify({ id: dbId, layerKey: layerKey || null });
        }

        return layerKey || null;
    }
}
